// CallBridge backed by F2's CallEngine. The engine factory is injected (lazy-loaded with
// SIP.js in production, fakes in tests). This adapter only translates: correlation, offer
// verification, command lifecycle and media stay inside the engine.
import type { Call, Capability, Device, EventEnvelope } from '@bcio-voice/contracts';
import type { ApiResult, CallControlApi, CallEngine, CallSnapshot, EngineSnapshot, PhoneStatus, Result } from '@bcio-voice/call-client/core';
import type { ApiClient } from '../lib/api.ts';
import { type EngineApi, createEngineApi } from './engineApi.ts';
import {
  type BridgeCall, type BridgeResult, type BridgeSnapshot, type BridgeTracks, type CallBridge, type MicPermission, NO_MEDIA, type PhoneState, type PhoneTransportView, type PlaceCallRequest,
  UNCONFIGURED_SNAPSHOT,
} from './bridge.ts';
import { mapTransport } from './transportView.ts';

export interface EngineFactoryInput {
  deviceId: string;
  userId: string;
  clientVersion: string;
  api: CallControlApi;
  /**
   * B1 `POST /devices/{id}/credentials` through the workspace ApiClient (canonical Device).
   * The factory wraps it in F2's rate-limited DeviceCredentialSource.
   */
  issueCredentials: (deviceId: string, signal?: AbortSignal) => Promise<ApiResult<unknown>>;
  /** The canonical Device from `POST /devices` when it carries a bootstrap; seeds the engine without rotating. */
  initialDevice?: Device;
  capabilities: () => Readonly<Record<string, Capability>> | null;
  allowedSipHosts: readonly string[];
}

export type EngineFactory = (input: EngineFactoryInput) => CallEngine;

export interface EngineBridgeOptions {
  api: Pick<ApiClient, 'request'>;
  userId: string;
  clientVersion: string;
  allowedSipHosts: readonly string[];
  loadFactory: () => Promise<EngineFactory>;
  capabilities: () => Readonly<Record<string, Capability>> | null;
  /** The engine re-registered after a gap or reconnect: refetch live state. */
  onResync?: () => void;
  /** The server revoked this device (heartbeat 404 or revoked bootstrap). */
  onRevoked?: () => void;
}

const PHONE: Record<PhoneStatus, PhoneState> = {
  idle: 'starting',
  starting: 'starting',
  blocked: 'blocked',
  needs_microphone: 'needs_microphone',
  in_another_tab: 'in_another_tab',
  connecting: 'connecting',
  registering: 'connecting',
  registered: 'registered',
  reconnecting: 'reconnecting',
  offline: 'offline',
  unregistered: 'stopped',
  revoked: 'revoked',
};

const toResult = (r: Result<unknown>): BridgeResult =>
  r.ok ? { ok: true } : { ok: false, code: r.error.code, message: r.error.message, serverCode: r.error.server_code ?? null };

const NO_ENGINE: BridgeResult = { ok: false, code: 'phone_not_ready', message: 'The phone is not connected on this browser.', serverCode: null };

function mapCall(c: CallSnapshot, transport: PhoneTransportView | null = null): BridgeCall {
  const latest = [...c.commands, ...(c.create ? [c.create] : [])].sort((a, b) => b.updated_at - a.updated_at)[0] ?? null;
  const since = Date.parse(c.created_at);
  return {
    key: c.key,
    callId: c.call_id,
    origin: c.origin,
    phase: c.phase,
    server: c.server,
    muted: c.muted,
    microphone: c.microphone,
    media: c.media,
    signaling: c.signaling,
    remoteAudio: c.remote_audio,
    endReason: c.end_reason,
    error: c.error ? { code: c.error.code, message: c.error.message, serverCode: c.error.server_code ?? null } : null,
    command: latest ? { kind: latest.kind, phase: latest.phase, error: latest.error } : null,
    transportEvents: transport ? transport.recent.filter(e => e.at >= since) : [],
  };
}

export function mapEngineSnapshot(s: EngineSnapshot, lastRefusal: string | null): BridgeSnapshot {
  const b = s.phone.bootstrap;
  const bootstrap = b === null ? null : 'reason_code' in b ? { status: b.status, reasonCode: b.reason_code, expiresAt: null } : { status: 'issued', reasonCode: null, expiresAt: b.expires_at };
  const permission: MicPermission = s.media.webrtc_supported ? s.media.permission : 'unsupported';
  const transport = mapTransport(s.phone.transport);
  return {
    engine: 'call-client',
    phone: {
      state: PHONE[s.phone.status],
      reason: s.phone.reason,
      attempt: s.phone.attempt,
      nextRetryAt: s.phone.next_retry_at,
      acceptingCalls: s.phone.accepting_calls,
      issues: [...s.phone.issues, ...(lastRefusal ? [`last_offer_refused:${lastRefusal}`] : [])],
      transport,
    },
    bootstrap,
    media: {
      permission,
      inputs: s.media.inputs.map((d, i) => ({ id: d.id, label: d.label || `Microphone ${i + 1}` })),
      outputs: s.media.outputs.map((d, i) => ({ id: d.id, label: d.label || `Speaker ${i + 1}` })),
      selectedInput: s.media.selected_input,
      selectedOutput: s.media.selected_output,
      outputSelection: s.media.output_selection,
      playback: s.media.playback,
    },
    calls: s.calls.map(c => mapCall(c, transport)),
  };
}

export class EngineBridge implements CallBridge {
  private engine: CallEngine | null = null;
  private disposers: (() => void)[] = [];
  private deviceId: string | null = null;
  private generation = 0;
  private stopped = false;
  private disposed = false;
  private revokedNotified = false;
  private lastRefusal: string | null = null;
  private engineRevision = -1;
  private snapshot: BridgeSnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly trackListeners = new Set<(update: BridgeTracks) => void>();
  private readonly engineApi: EngineApi;

  constructor(private readonly options: EngineBridgeOptions) {
    this.engineApi = createEngineApi(options.api);
    this.snapshot = this.unavailable('no_device', null);
  }

  getSnapshot = (): BridgeSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  attachDevice(device: Device | null): void {
    if (this.disposed) return;
    if (!device || device.revoked_at) {
      void this.detach('no_device', null);
      return;
    }
    const id = device.id.toLowerCase();
    // Same device: the engine refreshes its own credentials; nothing to restart.
    if (this.deviceId === id && (this.engine || this.generation > 0)) return;
    const detached = this.detach('no_device', null);
    const generation = this.generation;
    void detached.then(() => {
      if (generation === this.generation && !this.disposed) return this.create(device);
    });
  }

  private async create(device: Device): Promise<void> {
    const generation = ++this.generation;
    const id = device.id.toLowerCase();
    this.deviceId = id;
    const bootstrap = device.bootstrap ? { status: device.bootstrap.status, reasonCode: device.bootstrap.status === 'unconfigured' ? device.bootstrap.reason_code : null, expiresAt: device.bootstrap.status === 'issued' ? device.bootstrap.expires_at : null } : null;
    if (!this.options.allowedSipHosts.length) {
      this.set(this.unavailable('sip_hosts_not_configured', bootstrap));
      return;
    }
    this.set({ ...this.unavailable(null, bootstrap), phone: { ...UNCONFIGURED_SNAPSHOT.phone, state: 'starting', reason: null } });
    let factory: EngineFactory;
    try {
      factory = await this.options.loadFactory();
    } catch {
      if (generation === this.generation) this.set(this.unavailable('call_engine_unavailable', bootstrap));
      return;
    }
    if (generation !== this.generation || this.disposed) return;
    let engine: CallEngine;
    try {
      engine = factory({
        deviceId: id,
        userId: this.options.userId,
        clientVersion: this.options.clientVersion,
        api: this.engineApi,
        issueCredentials: (deviceId, signal) => this.engineApi.issueCredentials(deviceId, signal),
        ...(device.bootstrap ? { initialDevice: device } : {}),
        capabilities: this.options.capabilities,
        allowedSipHosts: this.options.allowedSipHosts,
      });
    } catch {
      this.set(this.unavailable('call_engine_unavailable', bootstrap));
      return;
    }
    this.engine = engine;
    this.revokedNotified = false;
    this.engineRevision = -1;
    this.disposers.push(
      engine.subscribe(() => this.sync()),
      engine.events.onAny(event => {
        if (event.type === 'resync_required') this.options.onResync?.();
        if (event.type === 'offer_refused') { this.lastRefusal = event.reason; this.sync(true); }
        // In a browser or the Electron renderer these are real MediaStreamTracks owned by the engine.
        if (event.type === 'tracks_changed') this.emitTracks({ callId: event.call_id, local: event.local as MediaStreamTrack | null, remote: event.remote as MediaStreamTrack | null });
      }),
    );
    this.sync(true);
    if (!this.stopped) void engine.start();
  }

  onTracks(listener: (update: BridgeTracks) => void): () => void {
    this.trackListeners.add(listener);
    return () => this.trackListeners.delete(listener);
  }

  private emitTracks(update: BridgeTracks): void {
    for (const l of [...this.trackListeners]) { try { l(update); } catch { /* a listener never breaks the call path */ } }
  }

  private async detach(reason: string | null, bootstrap: BridgeSnapshot['bootstrap']): Promise<void> {
    this.generation += 1;
    const engine = this.engine;
    this.engine = null;
    this.deviceId = null;
    for (const d of this.disposers.splice(0)) { try { d(); } catch { /* ignore */ } }
    if (engine) this.emitTracks({ callId: null, local: null, remote: null });
    if (!this.disposed) this.set(this.unavailable(reason, bootstrap));
    if (engine) await engine.dispose();
  }

  private sync(force = false): void {
    const engine = this.engine;
    if (!engine) return;
    const s = engine.getSnapshot();
    if (!force && s.revision === this.engineRevision) return;
    this.engineRevision = s.revision;
    this.set(mapEngineSnapshot(s, this.lastRefusal));
    if (s.phone.status === 'revoked' && !this.revokedNotified) {
      this.revokedNotified = true;
      this.options.onRevoked?.();
    }
  }

  private unavailable(reason: string | null, bootstrap: BridgeSnapshot['bootstrap']): BridgeSnapshot {
    return { ...UNCONFIGURED_SNAPSHOT, engine: 'call-client', phone: { ...UNCONFIGURED_SNAPSHOT.phone, reason }, bootstrap, media: NO_MEDIA };
  }

  private set(next: BridgeSnapshot): void {
    this.snapshot = next;
    for (const l of [...this.listeners]) l();
  }

  async stop(): Promise<void> {
    this.stopped = true;
    await this.engine?.unregister();
  }

  async releaseDevice(): Promise<void> { await this.detach('no_device', null); }

  start(): void {
    this.stopped = false;
    void this.engine?.start();
  }

  retryNow(): void {
    this.engine?.retryNow();
  }

  takeOver(): void {
    this.engine?.takeOverPhone();
  }

  async requestMicrophone(): Promise<BridgeResult> {
    if (!this.engine) return NO_ENGINE;
    return toResult(await this.engine.requestMicrophonePermission());
  }

  async refreshDevices(): Promise<void> {
    await this.engine?.refreshDevices();
  }

  async selectInput(id: string | null): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.selectInput(id)) : NO_ENGINE;
  }

  async selectOutput(id: string | null): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.selectOutput(id)) : NO_ENGINE;
  }

  async resumeAudio(): Promise<void> {
    await this.engine?.resumeAudioPlayback();
  }

  async placeCall(request: PlaceCallRequest): Promise<BridgeResult & { key?: string }> {
    if (!this.engine) return NO_ENGINE;
    const placed = await this.engine.placeCall({
      destination: request.destination,
      ...(request.from_number_id ? { from_number_id: request.from_number_id } : {}),
      ...(request.contact_id ? { contact_id: request.contact_id } : {}),
      ...(request.calling_session ? { calling_session: request.calling_session } : {}),
    });
    return placed.ok ? { ok: true, key: placed.value.key } : toResult(placed);
  }

  async answer(key: string): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.accept(key)) : NO_ENGINE;
  }

  async decline(key: string): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.reject(key)) : NO_ENGINE;
  }

  async hangup(key: string): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.hangup(key)) : NO_ENGINE;
  }

  async hold(key: string): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.hold(key)) : NO_ENGINE;
  }

  async resume(key: string): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.resume(key)) : NO_ENGINE;
  }

  async sendDtmf(key: string, digits: string): Promise<BridgeResult> {
    return this.engine ? toResult(await this.engine.sendDtmf(key, digits)) : NO_ENGINE;
  }

  setMuted(key: string, muted: boolean): BridgeResult {
    return this.engine ? toResult(this.engine.setMuted(key, muted)) : NO_ENGINE;
  }

  dismiss(key: string): void {
    this.engine?.dismiss(key);
  }

  applyServerCall(call: Call): void {
    this.engine?.applyServerCall(call);
  }

  notifyServerEvent(event: EventEnvelope): void {
    this.engine?.notifyServerEvent(event);
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    await this.detach(null, null);
    this.disposed = true;
    this.listeners.clear();
    this.trackListeners.clear();
  }
}
